/**
 * 制作工作台 · 左侧资料面板:角色卡 / NPC / 场地
 */
import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ChevronDown,
  ImagePlus,
  MapPin,
  Package,
  Pencil,
  Plus,
  Sparkles,
  User,
  Users,
  X,
} from 'lucide-react';
import { useVideoStyles } from '@/hooks/useVideoStyles';
import { api } from '@/lib/api';
import { cn } from '@/lib/cn';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from '@/components/ui/toast';
import type {
  DirectorCharacterCard,
  DirectorEpisodePlan,
  DirectorLocation,
  DirectorNpcCard,
  DirectorProp,
  ProductionConfig,
} from './types';
import { CharacterProfileDialog } from './CharacterProfileDialog';
import { LocationLockDialog } from './LocationLockDialog';
import { PropLockDialog } from './PropLockDialog';

interface Props {
  episodeId: string;
  characters: DirectorCharacterCard[];
  npcs: DirectorNpcCard[];
  locations: DirectorLocation[];
  props?: DirectorProp[];
  production?: ProductionConfig | null;
  episodes?: DirectorEpisodePlan[];
}

export function CastPanel({
  episodeId,
  characters,
  npcs,
  locations,
  props = [],
  production,
  episodes,
}: Props) {
  const qc = useQueryClient();
  const [activeCharId, setActiveCharId] = useState<string | null>(null);
  const [activeNpcId, setActiveNpcId] = useState<string | null>(null);
  const [activeLocationId, setActiveLocationId] = useState<string | null>(null);
  const [activePropId, setActivePropId] = useState<string | null>(null);
  const [editPropId, setEditPropId] = useState<string | null>(null);
  const [editingProduction, setEditingProduction] = useState(false);
  const [openSection, setOpenSection] = useState<Record<string, boolean>>({
    characters: true,
    npcs: false,
    locations: false,
    props: false,
  });
  const [showNewNpcForm, setShowNewNpcForm] = useState(false);
  const [newNpcName, setNewNpcName] = useState('');
  const [newNpcRole, setNewNpcRole] = useState('');
  const [newNpcGender, setNewNpcGender] = useState('');
  const [newNpcAppearance, setNewNpcAppearance] = useState('');

  const [showNewPropForm, setShowNewPropForm] = useState(false);
  const [newPropName, setNewPropName] = useState('');
  const [newPropCategory, setNewPropCategory] = useState('');
  const [newPropMaterial, setNewPropMaterial] = useState('');
  const [newPropStyle, setNewPropStyle] = useState('');
  const [newPropSize, setNewPropSize] = useState('');

  const createNpcMut = useMutation({
    mutationFn: () =>
      api
        .post(`director/episodes/${episodeId}/director/npcs`, {
          json: {
            name: newNpcName.trim(),
            role: newNpcRole.trim() || null,
            gender: newNpcGender || null,
            appearance: newNpcAppearance.trim(),
          },
        })
        .json(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['director', 'plan', episodeId] });
      setShowNewNpcForm(false);
      setNewNpcName('');
      setNewNpcRole('');
      setNewNpcGender('');
      setNewNpcAppearance('');
      toast.success('NPC 已创建');
    },
    onError: (e: Error) => toast.error('创建失败', e.message || '请重试'),
  });

  const deleteNpcMut = useMutation({
    mutationFn: (npcId: string) =>
      api.delete(`director/episodes/${episodeId}/director/npcs/${npcId}`).json(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['director', 'plan', episodeId] });
      toast.success('NPC 已删除');
    },
    onError: (e: Error) => toast.error('删除失败', e.message || '请重试'),
  });

  const createPropMut = useMutation({
    mutationFn: () =>
      api
        .post(`director/episodes/${episodeId}/director/props`, {
          json: {
            name: newPropName.trim(),
            category: newPropCategory.trim() || null,
            material: newPropMaterial.trim() || null,
            style: newPropStyle.trim() || null,
            size: newPropSize.trim() || null,
          },
        })
        .json(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['director', 'plan', episodeId] });
      setShowNewPropForm(false);
      setNewPropName('');
      setNewPropCategory('');
      setNewPropMaterial('');
      setNewPropStyle('');
      setNewPropSize('');
      toast.success('道具已创建');
    },
    onError: (e: Error) => toast.error('创建失败', e.message || '请重试'),
  });

  const deletePropMut = useMutation({
    mutationFn: (propId: string) =>
      api.delete(`director/episodes/${episodeId}/director/props/${propId}`).json(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['director', 'plan', episodeId] });
      toast.success('道具已删除');
    },
    onError: (e: Error) => toast.error('删除失败', e.message || '请重试'),
  });

  // 始终从最新 characters 取出 activeChar,避免轮询后拿到老快照(profileGroups 不刷新)
  const activeChar = useMemo(
    () => characters.find((c) => c.id === activeCharId) ?? null,
    [characters, activeCharId],
  );
  const activeNpc = useMemo(
    () => npcs.find((n) => n.id === activeNpcId) ?? null,
    [npcs, activeNpcId],
  );
  const [editLocationId, setEditLocationId] = useState<string | null>(null);
  const editLocation = useMemo(
    () => locations.find((l) => l.id === editLocationId) ?? null,
    [locations, editLocationId],
  );
  const activeLocation = useMemo(
    () => locations.find((l) => l.id === activeLocationId) ?? null,
    [locations, activeLocationId],
  );
  const activeProp = useMemo(
    () => props.find((p) => p.id === activePropId) ?? null,
    [props, activePropId],
  );
  const editProp = useMemo(
    () => props.find((p) => p.id === editPropId) ?? null,
    [props, editPropId],
  );

  return (
    <aside className="flex w-[300px] shrink-0 flex-col overflow-y-auto border-r border-outline-variant/20 bg-surface">
      <ProductionBadge
        production={production}
        onEdit={() => setEditingProduction(true)}
      />

      <Section
        icon={<User className="h-4 w-4 text-brand-500" />}
        label={`角色卡 · ${characters.length}`}
        open={openSection.characters}
        onToggle={() => setOpenSection((s) => ({ ...s, characters: !s.characters }))}
      >
        <div className="space-y-2 px-3 pb-3">
          {characters.length === 0 ? (
            <p className="text-[11px] text-neutral-400">暂无</p>
          ) : (
            characters.map((c) => (
              <CharacterRow
                key={c.id}
                episodeId={episodeId}
                character={c}
                onPick={() => setActiveCharId(c.id)}
              />
            ))
          )}
        </div>
      </Section>

      <Section
        icon={<Users className="h-4 w-4 text-brand-500" />}
        label={`NPC · ${npcs.length}`}
        open={openSection.npcs}
        onToggle={() => setOpenSection((s) => ({ ...s, npcs: !s.npcs }))}
      >
        <div className="space-y-2 px-3 pb-3">
          {npcs.length === 0 ? (
            <p className="text-[11px] text-neutral-400">暂无</p>
          ) : (
            npcs.map((n) => (
              <NpcRow
                key={n.id}
                npc={n}
                onPick={() => setActiveNpcId(n.id)}
                onDelete={() => deleteNpcMut.mutate(n.id)}
              />
            ))
          )}
          {showNewNpcForm ? (
            <div className="mt-2 rounded-lg border border-outline-variant/40 bg-surface-variant/30 p-3 space-y-2">
              <Input
                value={newNpcName}
                onChange={(e) => setNewNpcName(e.target.value)}
                placeholder="NPC 名称（必填）"
                className="text-xs"
              />
              <Input
                value={newNpcRole}
                onChange={(e) => setNewNpcRole(e.target.value)}
                placeholder="角色定位（如：路人甲、店主）"
                className="text-xs"
              />
              <div className="flex gap-1.5">
                {['男', '女', '其他'].map((g) => (
                  <button
                    key={g}
                    type="button"
                    onClick={() => setNewNpcGender((prev) => prev === g ? '' : g)}
                    className={cn(
                      'rounded-full border px-2 py-0.5 text-[11px] transition',
                      newNpcGender === g
                        ? 'border-brand-500 bg-brand-500/10 text-brand-600'
                        : 'border-outline-variant text-neutral-500 hover:border-outline',
                    )}
                  >{g}</button>
                ))}
              </div>
              <textarea
                value={newNpcAppearance}
                onChange={(e) => setNewNpcAppearance(e.target.value)}
                placeholder="外貌描述（体型、发型、服装特征等）"
                rows={2}
                className="w-full resize-none rounded border border-outline-variant bg-surface px-2 py-1 text-xs placeholder:text-outline focus:border-brand-500 focus:outline-none"
              />
              <div className="flex justify-end gap-1.5">
                <Button variant="ghost" size="sm" onClick={() => { setShowNewNpcForm(false); setNewNpcName(''); setNewNpcRole(''); setNewNpcGender(''); setNewNpcAppearance(''); }}>
                  取消
                </Button>
                <Button
                  variant="agent"
                  agentColor="director"
                  size="sm"
                  disabled={!newNpcName.trim() || createNpcMut.isPending}
                  onClick={() => createNpcMut.mutate()}
                >
                  {createNpcMut.isPending ? '创建中…' : '创建 NPC'}
                </Button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setShowNewNpcForm(true)}
              className="mt-1 flex w-full items-center gap-1 rounded border border-dashed border-outline-variant/50 px-2 py-1 text-[11px] text-neutral-400 hover:border-brand-400 hover:text-brand-500 transition"
            >
              <Plus className="h-3 w-3" /> 新增 NPC
            </button>
          )}
        </div>
      </Section>

      <Section
        icon={<MapPin className="h-4 w-4 text-brand-500" />}
        label={`场地 · ${locations.length}`}
        open={openSection.locations}
        onToggle={() => setOpenSection((s) => ({ ...s, locations: !s.locations }))}
      >
        <div className="space-y-2 px-3 pb-3">
          {locations.length === 0 ? (
            <p className="text-[11px] text-neutral-400">暂无</p>
          ) : (
            locations.map((l) => {
              const establishing =
                l.lockedImages?.find((li) => li.variant === 'establishing')?.url ||
                l.lockedImageUrl ||
                null;
              const lockedCount = (l.lockedImages ?? []).length;
              return (
                <div
                  key={l.id}
                  className="overflow-hidden rounded-md border border-outline-variant/30 bg-surface-variant/30 text-xs"
                >
                  <button
                    type="button"
                    onClick={() => setActiveLocationId(l.id)}
                    className="w-full p-2 text-left transition hover:bg-brand-50/30"
                  >
                    <div className="flex items-start gap-2">
                      <div
                        className={
                          'h-12 w-16 shrink-0 overflow-hidden rounded border bg-neutral-100 ' +
                          (establishing
                            ? 'border-brand-400 ring-1 ring-brand-200'
                            : 'border-outline-variant/40')
                        }
                      >
                        {establishing ? (
                          <img
                            src={establishing}
                            alt={l.name}
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <span className="flex h-full w-full items-center justify-center text-neutral-400">
                            <ImagePlus className="h-5 w-5" />
                          </span>
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1">
                          <span className="truncate font-medium text-neutral-800">
                            {l.name}
                          </span>
                          {lockedCount > 0 && (
                            <span className="rounded bg-brand-500 px-1 py-0 text-[9px] text-white">
                              {lockedCount}
                            </span>
                          )}
                        </div>
                        {(l.atmosphere || l.lighting) && (
                          <p className="mt-0.5 line-clamp-1 text-[10px] text-neutral-500">
                            {[l.atmosphere, l.lighting].filter(Boolean).join(' · ')}
                          </p>
                        )}
                        {l.props.length > 0 && (
                          <div className="mt-1 flex flex-wrap gap-1">
                            {l.props.slice(0, 4).map((p) => (
                              <span
                                key={p}
                                className="rounded bg-brand-50 px-1 py-0 text-[9px] text-brand-700"
                              >
                                {p}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </button>
                  <div className="flex border-t border-outline-variant/30">
                    <button
                      type="button"
                      onClick={() => setActiveLocationId(l.id)}
                      className="flex-1 py-1 text-center text-[10px] text-neutral-500 transition hover:bg-brand-50 hover:text-brand-600"
                    >
                      {establishing ? '查看定场图' : '+ 定场图'}
                    </button>
                    <div className="w-px bg-outline-variant/30" />
                    <button
                      type="button"
                      onClick={() => setEditLocationId(l.id)}
                      className="flex items-center justify-center gap-0.5 px-2 py-1 text-[10px] text-neutral-500 transition hover:bg-brand-50 hover:text-brand-600"
                    >
                      <Pencil className="h-2.5 w-2.5" /> 编辑
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </Section>

      <Section
        icon={<Package className="h-4 w-4 text-brand-500" />}
        label={`道具 · ${props.length}`}
        open={openSection.props}
        onToggle={() => setOpenSection((s) => ({ ...s, props: !s.props }))}
      >
        <div className="space-y-2 px-3 pb-3">
          {props.length === 0 ? (
            <p className="text-[11px] text-neutral-400">暂无道具</p>
          ) : (
            props.map((p) => {
              const frontImg =
                p.lockedImageUrl ||
                null;
              const lockedCount = p.lockedImageUrl ? 1 : 0;
              return (
                <div
                  key={p.id}
                  className="group overflow-hidden rounded-md border border-outline-variant/30 bg-surface-variant/30 text-xs"
                >
                  <button
                    type="button"
                    onClick={() => setActivePropId(p.id)}
                    className="w-full p-2 text-left transition hover:bg-brand-50/30 relative"
                  >
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); deletePropMut.mutate(p.id); }}
                      className="absolute right-1 top-1 rounded p-0.5 text-neutral-300 opacity-0 transition group-hover:opacity-100 hover:text-red-500"
                      title="删除道具"
                    >
                      <X className="h-3 w-3" />
                    </button>
                    <div className="flex items-start gap-2">
                      <div
                        className={
                          'h-12 w-16 shrink-0 overflow-hidden rounded border bg-neutral-100 ' +
                          (frontImg
                            ? 'border-brand-400 ring-1 ring-brand-200'
                            : 'border-outline-variant/40')
                        }
                      >
                        {frontImg ? (
                          <img
                            src={frontImg}
                            alt={p.name}
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <span className="flex h-full w-full items-center justify-center text-neutral-400">
                            <ImagePlus className="h-5 w-5" />
                          </span>
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1">
                          <span className="truncate font-medium text-neutral-800">
                            {p.name}
                          </span>
                          {lockedCount > 0 && (
                            <span className="rounded bg-brand-500 px-1 py-0 text-[9px] text-white">
                              {lockedCount}
                            </span>
                          )}
                        </div>
                        {(p.category || p.material || p.size) && (
                          <p className="mt-0.5 line-clamp-1 text-[10px] text-neutral-500">
                            {[p.category, p.material, p.size].filter(Boolean).join(' · ')}
                          </p>
                        )}
                        {(p.looks ?? []).length > 0 && (
                          <div className="mt-1 flex flex-wrap gap-1">
                            {(p.looks ?? []).slice(0, 3).map((lk) => (
                              <span
                                key={lk.id}
                                className="rounded bg-violet-50 px-1 py-0 text-[9px] text-violet-700"
                              >
                                {lk.label}
                              </span>
                            ))}
                            {(p.looks ?? []).length > 3 && (
                              <span className="text-[9px] text-neutral-400">
                                +{(p.looks ?? []).length - 3}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </button>
                  <div className="flex border-t border-outline-variant/30">
                    <button
                      type="button"
                      onClick={() => setActivePropId(p.id)}
                      className="flex-1 py-1 text-center text-[10px] text-neutral-500 transition hover:bg-brand-50 hover:text-brand-600"
                    >
                      {frontImg ? '查看三视图' : '+ 设定图'}
                    </button>
                    <div className="w-px bg-outline-variant/30" />
                    <button
                      type="button"
                      onClick={() => setEditPropId(p.id)}
                      className="flex items-center justify-center gap-0.5 px-2 py-1 text-[10px] text-neutral-500 transition hover:bg-brand-50 hover:text-brand-600"
                    >
                      <Pencil className="h-2.5 w-2.5" /> 编辑
                    </button>
                  </div>
                </div>
              );
            })
          )}
          {showNewPropForm ? (
            <div className="mt-2 rounded-lg border border-outline-variant/40 bg-surface-variant/30 p-3 space-y-2">
              <input
                type="text"
                value={newPropName}
                onChange={(e) => setNewPropName(e.target.value)}
                placeholder="道具名称（必填）"
                className="w-full rounded border border-outline-variant/40 bg-surface px-2 py-1 text-xs focus:border-brand-400 focus:outline-none"
              />
              <input
                type="text"
                value={newPropCategory}
                onChange={(e) => setNewPropCategory(e.target.value)}
                placeholder="类别（如：武器、饰品、法器）"
                className="w-full rounded border border-outline-variant/40 bg-surface px-2 py-1 text-xs focus:border-brand-400 focus:outline-none"
              />
              <input
                type="text"
                value={newPropMaterial}
                onChange={(e) => setNewPropMaterial(e.target.value)}
                placeholder="材质（如：金属、玉石、木质）"
                className="w-full rounded border border-outline-variant/40 bg-surface px-2 py-1 text-xs focus:border-brand-400 focus:outline-none"
              />
              <input
                type="text"
                value={newPropStyle}
                onChange={(e) => setNewPropStyle(e.target.value)}
                placeholder="风格（如：古风、科幻、奇幻）"
                className="w-full rounded border border-outline-variant/40 bg-surface px-2 py-1 text-xs focus:border-brand-400 focus:outline-none"
              />
              <input
                type="text"
                value={newPropSize}
                onChange={(e) => setNewPropSize(e.target.value)}
                placeholder="尺寸描述（如：小型手持、中型桌面摆件、大型落地）"
                className="w-full rounded border border-outline-variant/40 bg-surface px-2 py-1 text-xs focus:border-brand-400 focus:outline-none"
              />
              <div className="flex justify-end gap-1.5">
                <Button variant="ghost" size="sm" onClick={() => { setShowNewPropForm(false); setNewPropName(''); setNewPropCategory(''); setNewPropMaterial(''); setNewPropStyle(''); setNewPropSize(''); }}>
                  取消
                </Button>
                <Button
                  variant="agent"
                  agentColor="director"
                  size="sm"
                  disabled={!newPropName.trim() || createPropMut.isPending}
                  onClick={() => createPropMut.mutate()}
                >
                  {createPropMut.isPending ? '创建中…' : '创建道具'}
                </Button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setShowNewPropForm(true)}
              className="mt-1 flex w-full items-center gap-1 rounded border border-dashed border-outline-variant/50 px-2 py-1 text-[11px] text-neutral-400 hover:border-brand-400 hover:text-brand-500 transition"
            >
              <Plus className="h-3 w-3" /> 新增道具
            </button>
          )}
        </div>
      </Section>

      {activeChar && (
        <CharacterProfileDialog
          open={!!activeChar}
          onOpenChange={(o) => {
            if (!o) setActiveCharId(null);
          }}
          episodeId={episodeId}
          character={activeChar}
          episodes={episodes}
        />
      )}

      {activeNpc && (
        <CharacterProfileDialog
          open={!!activeNpc}
          onOpenChange={(o) => {
            if (!o) setActiveNpcId(null);
          }}
          episodeId={episodeId}
          character={activeNpc}
          kind="npc"
          episodes={episodes}
        />
      )}

      {activeLocation && (
        <LocationLockDialog
          open={!!activeLocation}
          onOpenChange={(o) => {
            if (!o) setActiveLocationId(null);
          }}
          episodeId={episodeId}
          location={activeLocation}
        />
      )}

      {activeProp && (
        <PropLockDialog
          open={!!activeProp}
          onOpenChange={(o) => {
            if (!o) setActivePropId(null);
          }}
          episodeId={episodeId}
          prop={activeProp}
        />
      )}

      {editingProduction && (
        <ProductionEditDialog
          episodeId={episodeId}
          initial={production ?? null}
          onClose={() => setEditingProduction(false)}
        />
      )}

      {editLocation && (
        <LocationEditDialog
          episodeId={episodeId}
          location={editLocation}
          onClose={() => setEditLocationId(null)}
        />
      )}

      {editProp && (
        <PropEditDialog
          episodeId={episodeId}
          prop={editProp}
          onClose={() => setEditPropId(null)}
        />
      )}
    </aside>
  );
}

function ProductionBadge({
  production,
  onEdit,
}: {
  production?: ProductionConfig | null;
  onEdit: () => void;
}) {
  if (!production) {
    return (
      <div className="flex items-center justify-between gap-2 border-b border-outline-variant/20 bg-amber-50 px-3 py-2 text-[11px] text-amber-700">
        <span className="flex items-center gap-1.5">
          <Sparkles className="h-3.5 w-3.5" />
          未设置影视风格 / 画面比例
        </span>
        <button
          type="button"
          onClick={onEdit}
          className="rounded bg-amber-500 px-2 py-0.5 text-[10px] font-medium text-white transition hover:bg-amber-600"
        >
          配置
        </button>
      </div>
    );
  }
  return (
    <div className="flex items-center justify-between gap-2 border-b border-outline-variant/20 bg-brand-50 px-3 py-2 text-[11px] text-brand-700">
      <span className="min-w-0 flex items-center gap-1.5">
        <Sparkles className="h-3.5 w-3.5 shrink-0" />
        <span className="truncate">
          {production.videoStyle} · {production.aspectRatio}
        </span>
      </span>
      <button
        type="button"
        onClick={onEdit}
        className="flex shrink-0 items-center gap-0.5 rounded px-1.5 py-0.5 text-[10px] text-brand-600 transition hover:bg-brand-100"
        title="编辑制作配置"
      >
        <Pencil className="h-3 w-3" />
        编辑
      </button>
    </div>
  );
}

function ProductionEditDialog({
  episodeId,
  initial,
  onClose,
}: {
  episodeId: string;
  initial: ProductionConfig | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const { data: VIDEO_STYLES = [] } = useVideoStyles();
  const VIDEO_STYLE_NAMES = VIDEO_STYLES.map((s) => s.name);
  const isChip = (s: string) => VIDEO_STYLE_NAMES.includes(s);
  const initialStyle = initial?.videoStyle ?? '二次元动漫';
  const [pickedChip, setPickedChip] = useState(
    isChip(initialStyle) ? initialStyle : '二次元动漫',
  );
  const [customStyle, setCustomStyle] = useState(
    !isChip(initialStyle) ? initialStyle : '',
  );
  const [aspectRatio, setAspectRatio] = useState<'9:16' | '16:9'>(
    (initial?.aspectRatio as '9:16' | '16:9') ?? '9:16',
  );
  const [safeEndingFrameEnabled, setSafeEndingFrameEnabled] = useState(
    Boolean(initial?.safeEndingFrameEnabled),
  );

  const effectiveStyle = customStyle.trim() || pickedChip;
  const ready = effectiveStyle.length > 0 && (aspectRatio === '9:16' || aspectRatio === '16:9');

  const mut = useMutation({
    mutationFn: () =>
      api
        .patch(`director/episodes/${episodeId}/director/production`, {
          json: { videoStyle: effectiveStyle, aspectRatio, safeEndingFrameEnabled },
        })
        .json<ProductionConfig>(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['director', 'plan', episodeId] });
      qc.invalidateQueries({ queryKey: ['director', 'status', episodeId] });
      onClose();
    },
  });

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-lg bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-outline-variant/30 px-4 py-3">
          <h3 className="text-sm font-semibold text-neutral-800">编辑制作配置</h3>
          <button
            type="button"
            onClick={onClose}
            className="text-neutral-400 hover:text-neutral-600"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="space-y-4 px-4 py-4">
          <div>
            <label className="mb-1.5 block text-xs font-medium text-neutral-700">
              影视风格
            </label>
            <div className="flex flex-wrap gap-1.5">
              {VIDEO_STYLES.map((style) => {
                const active = !customStyle && pickedChip === style.name;
                return (
                  <button
                    key={style.name}
                    type="button"
                    title={`${style.tagline}\n适合: ${style.suitableFor.join('、')}`}
                    onClick={() => {
                      setPickedChip(style.name);
                      setCustomStyle('');
                    }}
                    className={
                      'rounded-full px-3 py-1 text-[11px] transition ' +
                      (active
                        ? 'bg-brand-500 text-white'
                        : 'bg-surface-variant/50 text-neutral-700 hover:bg-brand-50')
                    }
                  >
                    {style.name}
                  </button>
                );
              })}
            </div>
            {/* 选中风格的说明 */}
            {!customStyle && pickedChip && (() => {
              const meta = VIDEO_STYLES.find((s) => s.name === pickedChip);
              if (!meta) return null;
              return (
                <div className="mt-1.5 rounded bg-surface-variant/30 px-2.5 py-1.5 text-[11px] text-neutral-600">
                  <span className="font-medium">{meta.tagline}</span>
                  <span className="ml-2 text-neutral-400">适合: {meta.suitableFor.join('、')}</span>
                </div>
              );
            })()}
            <input
              type="text"
              value={customStyle}
              onChange={(e) => setCustomStyle(e.target.value)}
              placeholder="自定义风格(可选,优先生效)"
              maxLength={40}
              className="mt-2 w-full rounded border border-outline-variant/40 bg-white px-2 py-1.5 text-xs text-neutral-800 focus:border-brand-400 focus:outline-none"
            />
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-medium text-neutral-700">
              画面比例
            </label>
            <div className="flex gap-2">
              {(['9:16', '16:9'] as const).map((a) => (
                <button
                  key={a}
                  type="button"
                  onClick={() => setAspectRatio(a)}
                  className={
                    'flex-1 rounded-md border px-3 py-2 text-xs transition ' +
                    (aspectRatio === a
                      ? 'border-brand-500 bg-brand-50 text-brand-700'
                      : 'border-outline-variant/40 bg-white text-neutral-700 hover:border-brand-300')
                  }
                >
                  {a === '9:16' ? '9:16 · 竖屏' : '16:9 · 横屏'}
                </button>
              ))}
            </div>
          </div>
          <div className="rounded-md border border-outline-variant/30 bg-surface-variant/20 px-3 py-2.5">
            <div className="mb-2 text-xs font-medium text-neutral-700">安全策略</div>
            <label className="flex cursor-pointer items-start justify-between gap-3">
              <span className="min-w-0">
                <span className="block text-xs font-medium text-neutral-700">
                  承接帧是否真人安全处理
                </span>
                <span className="mt-0.5 block text-[11px] leading-snug text-neutral-500">
                  开启后,选中视频时会生成低真人细节、干净可衔接的安全承接帧;关闭时直接使用原始截图。
                </span>
              </span>
              <input
                type="checkbox"
                checked={safeEndingFrameEnabled}
                onChange={(e) => setSafeEndingFrameEnabled(e.target.checked)}
                className="mt-0.5 h-4 w-4 shrink-0 rounded border-outline-variant/50 text-brand-600 focus:ring-brand-400"
              />
            </label>
          </div>
          {mut.isError && (
            <p className="text-[11px] text-red-600">
              {(mut.error as Error)?.message ?? '保存失败'}
            </p>
          )}
        </div>
        <div className="flex justify-end gap-2 border-t border-outline-variant/30 bg-surface-variant/30 px-4 py-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded px-3 py-1.5 text-xs text-neutral-600 hover:bg-surface-variant/60"
          >
            取消
          </button>
          <button
            type="button"
            disabled={!ready || mut.isPending}
            onClick={() => mut.mutate()}
            className="rounded bg-brand-500 px-3 py-1.5 text-xs text-white transition hover:bg-brand-600 disabled:opacity-50"
          >
            {mut.isPending ? '保存中…' : '保存'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Section({
  icon,
  label,
  open,
  onToggle,
  children,
}: {
  icon: React.ReactNode;
  label: string;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="border-b border-outline-variant/20">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center justify-between px-3 py-2.5 text-left transition hover:bg-surface-variant/30"
      >
        <span className="flex items-center gap-2 text-sm font-semibold text-neutral-800">
          {icon}
          {label}
        </span>
        <ChevronDown
          className={
            'h-4 w-4 text-neutral-400 transition ' +
            (open ? 'rotate-0' : '-rotate-90')
          }
        />
      </button>
      {open && children}
    </div>
  );
}

function NpcRow({
  npc,
  onPick,
  onDelete,
}: {
  npc: DirectorNpcCard;
  onPick: () => void;
  onDelete?: () => void;
}) {
  const profileUrl = npc.profileImageUrl ?? null;
  const hasProfile = !!profileUrl;

  return (
    <div className="group w-full overflow-hidden rounded-md border border-outline-variant/30 bg-surface-variant/30 text-left transition hover:border-brand-300 hover:bg-brand-50/40">
      <button
        type="button"
        onClick={onPick}
        className="block w-full text-left"
        title={hasProfile ? '点击查看 / 更换人物设定' : '点击设置人物设定'}
      >
        <div className="flex gap-2 p-2">
          <div
            className={
              'relative h-14 w-14 shrink-0 overflow-hidden rounded border bg-neutral-100 ' +
              (hasProfile
                ? 'border-brand-400 ring-1 ring-brand-200'
                : 'border-outline-variant/40')
            }
          >
            {profileUrl ? (
              <img
                src={profileUrl}
                alt={npc.name}
                className="h-full w-full object-cover"
              />
            ) : (
              <span className="flex h-full w-full items-center justify-center text-neutral-400">
                <ImagePlus className="h-5 w-5" />
              </span>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-baseline justify-between gap-1">
              <span className="flex min-w-0 items-baseline gap-1">
                <span className="truncate text-xs font-semibold text-neutral-800">
                  {npc.name}
                </span>
                {npc.gender && <GenderChip gender={npc.gender} />}
              </span>
              <span className="flex shrink-0 items-center gap-1">
                {npc.role && (
                  <span className="text-[10px] text-brand-600">
                    {npc.role}
                  </span>
                )}
                {onDelete && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onDelete(); }}
                    className="rounded p-0.5 text-neutral-300 opacity-0 transition group-hover:opacity-100 hover:text-red-500"
                    title="删除 NPC"
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
              </span>
            </div>
            <p className="mt-0.5 line-clamp-2 text-[11px] leading-relaxed text-neutral-500">
              {npc.appearance}
            </p>
          </div>
        </div>
      </button>
      <div className="block w-full border-t border-outline-variant/30 px-2 py-1 text-center text-[10px] text-neutral-500 transition group-hover:bg-brand-50 group-hover:text-brand-600">
        {hasProfile ? '点击查看 / 更换人物设定' : '+ 设置人物设定'}
      </div>
    </div>
  );
}

function CharacterRow({
  character,
  onPick,
}: {
  episodeId: string;
  character: DirectorCharacterCard;
  onPick: () => void;
}) {
  const profileUrl = character.looks?.[0]?.profileImageUrl ?? character.profileImageUrl ?? null;
  const hasProfile = !!profileUrl;
  const lookCount = character.looks?.length ?? 0;

  return (
    <button
      type="button"
      onClick={onPick}
      className="block w-full overflow-hidden rounded-md border border-outline-variant/30 bg-surface-variant/30 text-left transition hover:border-brand-300 hover:bg-brand-50/40"
      title={hasProfile ? '点击查看 / 更换人物设定' : '点击设置人物设定'}
    >
      <div className="flex gap-2 p-2">
        <div
          className={
            'relative h-14 w-14 shrink-0 overflow-hidden rounded border bg-neutral-100 ' +
            (hasProfile
              ? 'border-brand-400 ring-1 ring-brand-200'
              : 'border-outline-variant/40')
          }
        >
          {profileUrl ? (
            <img
              src={profileUrl}
              alt={character.name}
              className="h-full w-full object-cover"
            />
          ) : (
            <span className="flex h-full w-full items-center justify-center text-neutral-400">
              <ImagePlus className="h-5 w-5" />
            </span>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline justify-between gap-1">
            <span className="flex min-w-0 items-baseline gap-1">
              <span className="truncate text-xs font-semibold text-neutral-800">
                {character.name}
              </span>
              {character.gender && <GenderChip gender={character.gender} />}
              {lookCount > 1 && (
                <span className="shrink-0 rounded bg-violet-100 px-1 py-0.5 text-[9px] leading-none text-violet-700">
                  {lookCount}形象
                </span>
              )}
            </span>
            {character.role && (
              <span className="shrink-0 text-[10px] text-brand-600">
                {character.role}
              </span>
            )}
          </div>
          <p className="mt-0.5 line-clamp-2 text-[11px] leading-relaxed text-neutral-500">
            {character.appearance}
          </p>
        </div>
      </div>
      <div className="block w-full border-t border-outline-variant/30 px-2 py-1 text-center text-[10px] text-neutral-500 transition group-hover:bg-brand-50 group-hover:text-brand-600">
        {hasProfile ? '点击查看 / 更换人物设定' : '+ 设置人物设定'}
      </div>
    </button>
  );
}

function LocationEditDialog({
  episodeId,
  location,
  onClose,
}: {
  episodeId: string;
  location: DirectorLocation;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [name, setName] = useState(location.name);
  const [atmosphere, setAtmosphere] = useState(location.atmosphere ?? '');
  const [lighting, setLighting] = useState(location.lighting ?? '');
  const [propsText, setPropsText] = useState(location.props.join('、'));
  const [notes, setNotes] = useState(location.notes ?? '');

  const mut = useMutation({
    mutationFn: () => {
      const body: Record<string, unknown> = {};
      if (name !== location.name) body.name = name;
      if (atmosphere !== (location.atmosphere ?? '')) body.atmosphere = atmosphere || null;
      if (lighting !== (location.lighting ?? '')) body.lighting = lighting || null;
      const newProps = propsText
        .split(/[、,，]/)
        .map((s) => s.trim())
        .filter(Boolean);
      if (JSON.stringify(newProps) !== JSON.stringify(location.props)) body.props = newProps;
      if (notes !== (location.notes ?? '')) body.notes = notes || null;
      return api
        .patch(`director/episodes/${episodeId}/director/locations/${location.id}`, {
          json: body,
        })
        .json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['director', 'plan', episodeId] });
      toast.success('场地信息已保存');
      onClose();
    },
    onError: (e: Error) => toast.error('保存失败', e.message || '请重试'),
  });

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-lg bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-outline-variant/30 px-4 py-3">
          <h3 className="text-sm font-semibold text-neutral-800">编辑场地</h3>
          <button
            type="button"
            onClick={onClose}
            className="text-neutral-400 hover:text-neutral-600"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="space-y-3 px-4 py-4">
          <div>
            <label className="mb-1 block text-xs font-medium text-neutral-700">
              场地名称
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full rounded border border-outline-variant/40 bg-white px-2 py-1.5 text-xs text-neutral-800 focus:border-brand-400 focus:outline-none"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-neutral-700">
              氛围
            </label>
            <input
              type="text"
              value={atmosphere}
              onChange={(e) => setAtmosphere(e.target.value)}
              placeholder="例：紧张压抑、温馨怀旧"
              className="w-full rounded border border-outline-variant/40 bg-white px-2 py-1.5 text-xs text-neutral-800 focus:border-brand-400 focus:outline-none"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-neutral-700">
              光线
            </label>
            <input
              type="text"
              value={lighting}
              onChange={(e) => setLighting(e.target.value)}
              placeholder="例：暖色调柔光、冷色高对比"
              className="w-full rounded border border-outline-variant/40 bg-white px-2 py-1.5 text-xs text-neutral-800 focus:border-brand-400 focus:outline-none"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-neutral-700">
              道具（顿号或逗号分隔）
            </label>
            <input
              type="text"
              value={propsText}
              onChange={(e) => setPropsText(e.target.value)}
              placeholder="例：破旧沙发、啤酒瓶、烟灰缸"
              className="w-full rounded border border-outline-variant/40 bg-white px-2 py-1.5 text-xs text-neutral-800 focus:border-brand-400 focus:outline-none"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-neutral-700">
              补充说明（辅助图片生成）
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="可为 AI 补充额外的场地描述、拍摄要求…"
              className="w-full resize-none rounded border border-outline-variant/40 bg-white px-2 py-1.5 text-xs text-neutral-800 focus:border-brand-400 focus:outline-none"
            />
          </div>
          {mut.isError && (
            <p className="text-[11px] text-red-600">
              {(mut.error as Error)?.message ?? '保存失败'}
            </p>
          )}
        </div>
        <div className="flex justify-end gap-2 border-t border-outline-variant/30 bg-surface-variant/30 px-4 py-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded px-3 py-1.5 text-xs text-neutral-600 hover:bg-surface-variant/60"
          >
            取消
          </button>
          <button
            type="button"
            disabled={!name.trim() || mut.isPending}
            onClick={() => mut.mutate()}
            className="rounded bg-brand-500 px-3 py-1.5 text-xs text-white transition hover:bg-brand-600 disabled:opacity-50"
          >
            {mut.isPending ? '保存中…' : '保存'}
          </button>
        </div>
      </div>
    </div>
  );
}

function PropEditDialog({
  episodeId,
  prop,
  onClose,
}: {
  episodeId: string;
  prop: DirectorProp;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [name, setName] = useState(prop.name);
  const [category, setCategory] = useState(prop.category ?? '');
  const [material, setMaterial] = useState(prop.material ?? '');
  const [style, setStyle] = useState(prop.style ?? '');
  const [size, setSize] = useState(prop.size ?? '');

  const mut = useMutation({
    mutationFn: () => {
      const body: Record<string, unknown> = {};
      if (name !== prop.name) body.name = name;
      if (category !== (prop.category ?? '')) body.category = category || null;
      if (material !== (prop.material ?? '')) body.material = material || null;
      if (style !== (prop.style ?? '')) body.style = style || null;
      if (size !== (prop.size ?? '')) body.size = size || null;
      if (Object.keys(body).length === 0) return Promise.resolve(null);
      return api
        .patch(`director/episodes/${episodeId}/director/props/${prop.id}`, { json: body })
        .json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['director', 'plan', episodeId] });
      toast.success('道具信息已保存');
      onClose();
    },
    onError: (e: Error) => toast.error('保存失败', e.message || '请重试'),
  });

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-lg bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-outline-variant/30 px-4 py-3">
          <h3 className="text-sm font-semibold text-neutral-800">编辑道具</h3>
          <button type="button" onClick={onClose} className="text-neutral-400 hover:text-neutral-600">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="space-y-3 px-4 py-4">
          <div>
            <label className="mb-1 block text-xs font-medium text-neutral-700">道具名称</label>
            <input
              type="text" value={name} onChange={(e) => setName(e.target.value)}
              className="w-full rounded border border-outline-variant/40 bg-white px-2 py-1.5 text-xs text-neutral-800 focus:border-brand-400 focus:outline-none"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-neutral-700">类别</label>
            <input
              type="text" value={category} onChange={(e) => setCategory(e.target.value)}
              placeholder="例：武器、饰品、法器"
              className="w-full rounded border border-outline-variant/40 bg-white px-2 py-1.5 text-xs text-neutral-800 focus:border-brand-400 focus:outline-none"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-neutral-700">材质</label>
            <input
              type="text" value={material} onChange={(e) => setMaterial(e.target.value)}
              placeholder="例：金属、玉石、木质"
              className="w-full rounded border border-outline-variant/40 bg-white px-2 py-1.5 text-xs text-neutral-800 focus:border-brand-400 focus:outline-none"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-neutral-700">风格</label>
            <input
              type="text" value={style} onChange={(e) => setStyle(e.target.value)}
              placeholder="例：古风、科幻、奇幻"
              className="w-full rounded border border-outline-variant/40 bg-white px-2 py-1.5 text-xs text-neutral-800 focus:border-brand-400 focus:outline-none"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-neutral-700">尺寸描述</label>
            <input
              type="text" value={size} onChange={(e) => setSize(e.target.value)}
              placeholder="例：小型手持、中型桌面摆件、大型落地"
              className="w-full rounded border border-outline-variant/40 bg-white px-2 py-1.5 text-xs text-neutral-800 focus:border-brand-400 focus:outline-none"
            />
            <p className="mt-0.5 text-[10px] text-neutral-400">
              尺寸信息会加入 @-mention 标签，帮助 AI 生成视频时还原正确比例
            </p>
          </div>
          {mut.isError && (
            <p className="text-[11px] text-red-600">{(mut.error as Error)?.message ?? '保存失败'}</p>
          )}
        </div>
        <div className="flex justify-end gap-2 border-t border-outline-variant/30 bg-surface-variant/30 px-4 py-3">
          <button type="button" onClick={onClose}
            className="rounded px-3 py-1.5 text-xs text-neutral-600 hover:bg-surface-variant/60">取消</button>
          <button type="button" disabled={!name.trim() || mut.isPending}
            onClick={() => mut.mutate()}
            className="rounded bg-brand-500 px-3 py-1.5 text-xs text-white transition hover:bg-brand-600 disabled:opacity-50">
            {mut.isPending ? '保存中…' : '保存'}
          </button>
        </div>
      </div>
    </div>
  );
}

function GenderChip({ gender }: { gender: string }) {
  const norm = gender.trim();
  if (!norm) return null;
  const tone =
    norm.startsWith('男')
      ? 'bg-sky-100 text-sky-700'
      : norm.startsWith('女')
        ? 'bg-rose-100 text-rose-700'
        : 'bg-neutral-100 text-neutral-600';
  return (
    <span className={`shrink-0 rounded px-1 py-0.5 text-[10px] leading-none ${tone}`}>
      {norm}
    </span>
  );
}
